import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { capitalised, countOf, type BlockNoun } from "../lib/blockNoun";
import {
  blockNounForVersion,
  getRun,
  invokeEvaluation,
  latestEvaluation,
  listFindings,
  listKernelOrigins,
  listOrganisations,
  myAuthority,
  promoteKernelFinding,
  runInput,
} from "../lib/api";
import { inputFingerprint } from "../lib/fingerprint";
import type {
  BlockResult,
  CapApplication,
  EvaluationResult,
  EvaluationRow,
  Finding,
  MembershipRole,
  Organisation,
  Qualification,
  Run,
} from "../lib/types";
import { useBreadcrumb } from "../shell/ContextHeader";
import { NoNamesHint } from "./NoNamesHint";
import { StateBadge } from "./RunOverview";

// Evaluation results, display-only (Release 1 session 5).
//
// "The kernel calculates; the console renders" - the architect's session 5
// authorization, and the whole design of this file. Everything numeric or
// judgemental on this screen is read VERBATIM from the persisted kernel
// snapshot (gate 1); nothing here computes coverage, qualification, scores
// or caps (gate 2); a reload re-reads the same persisted row and renders the
// same display (gate 3).
//
// The one thing this screen derives itself is an IDENTITY, not a result: the
// staleness fingerprint (gate 9) - the kernel's own canonicalisation of the
// current assembled input, hashed, compared against the `input_hash` the
// evaluation recorded. Match means "this result reflects the responses as
// they stand"; mismatch means "responses moved since evaluation" and the
// result is flagged stale rather than presented as current.
//
// Null-state fidelity (gate 5) is load-bearing everywhere below:
//   - no evaluation row       -> "not yet evaluated", never an empty result
//   - score null              -> "not scored", never 0
//   - verdict null            -> the withheld reason, never a blank number
//   - currency indeterminate  -> said plainly, never silently "current"

const num = (n: number | null): string =>
  n === null ? "—" : Number.isInteger(n) ? String(n) : n.toFixed(2);

const QUAL_STYLE: Record<
  Qualification["status"],
  { label: string; bg: string; fg: string }
> = {
  complete: { label: "complete", bg: "var(--state-ok-soft, #e6f4ea)", fg: "var(--state-ok)" },
  qualified: { label: "qualified", bg: "var(--lf-amber-20)", fg: "var(--lf-amber-deep)" },
  incomplete: { label: "incomplete", bg: "var(--surface-sunken, #eee)", fg: "var(--text-muted)" },
  invalidated: { label: "invalidated", bg: "var(--state-danger-soft)", fg: "var(--state-danger)" },
};

/** Stable product copy for the kernel's qualification reason codes (ADR-16).
 *  Wording only - the codes and their meaning are the kernel's. */
const REASON_COPY: Record<string, string> = {
  limited_coverage: "coverage is below the instrument's minimum",
  insufficient_evidence: "required evidence is not linked",
  qualified_limited_applicability: "applicability was limited on this run",
};

function QualBadge({ q }: { q: Qualification }) {
  const s = QUAL_STYLE[q.status];
  return (
    <span className="badge" style={{ background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

const SEVERITY_COLOR: Record<Finding["severity"], string> = {
  critical: "var(--state-danger)",
  high: "var(--state-danger)",
  medium: "var(--lf-amber-deep)",
  low: "var(--text-muted)",
};

/** How current the displayed evaluation is, relative to the responses. */
type Currency =
  | { kind: "current" }
  | { kind: "stale" }
  | { kind: "unknown"; reason: string };

/** Which kernel findings of THIS evaluation already carry an active managed
 *  expression (F4.3): origin rows joined with their findings' status —
 *  withdrawn expressions free the key for re-promotion as the next
 *  generation, so only non-withdrawn ones count as "in the register". */
interface PromotionState {
  activeKeys: Set<string>;
  generationByKey: Map<string, number>;
}

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      org: Organisation;
      run: Run;
      role: MembershipRole | null;
      evaluation: EvaluationRow | null;
      currency: Currency | null; // null when there is no evaluation to date
      promotions: PromotionState;
      noun: BlockNoun;
    };

export function EvaluationResults() {
  const { orgId, runId } = useParams<{ orgId: string; runId: string }>();
  const { setTrail } = useBreadcrumb();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [evaluating, setEvaluating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId || !runId) return;
    try {
      const [orgs, role, run, evaluation] = await Promise.all([
        listOrganisations(),
        myAuthority(orgId),
        getRun(runId),
        latestEvaluation(runId),
      ]);
      const org = orgs.find((o) => o.id === orgId);
      if (!org || !run || run.organisation_id !== orgId) {
        setState({ kind: "error", message: "run not found, or you are not a member" });
        return;
      }

      // Gate 9. Failure to determine currency is ITS OWN state: an error here
      // must never let a possibly-stale result read as current.
      let currency: Currency | null = null;
      const promotions: PromotionState = {
        activeKeys: new Set(),
        generationByKey: new Map(),
      };
      if (evaluation) {
        try {
          const fp = await inputFingerprint(await runInput(runId));
          currency = fp === evaluation.input_hash ? { kind: "current" } : { kind: "stale" };
        } catch (e) {
          currency = { kind: "unknown", reason: (e as Error).message };
        }
        const [origins, registerFindings] = await Promise.all([
          listKernelOrigins(evaluation.id),
          listFindings(runId),
        ]);
        const statusById = new Map(registerFindings.map((f) => [f.id, f.status]));
        for (const o of origins) {
          promotions.generationByKey.set(
            o.kernel_finding_key,
            Math.max(promotions.generationByKey.get(o.kernel_finding_key) ?? 0, o.promotion_generation),
          );
          if (statusById.get(o.finding_id) !== "withdrawn")
            promotions.activeKeys.add(o.kernel_finding_key);
        }
      }

      const noun = await blockNounForVersion(run.instrument_version_id);
      setState({ kind: "ready", org, run, role, evaluation, currency, promotions, noun });
      setTrail([
        { label: "Clients & engagements", to: "/clients" },
        { label: org.name, to: `/clients/${org.id}` },
        { label: run.title ?? "Untitled run", to: `/clients/${org.id}/runs/${run.id}` },
        { label: "Results" },
      ]);
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    }
  }, [orgId, runId, setTrail]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") return <p>Loading…</p>;
  if (state.kind === "error")
    return <div className="error-banner">Could not load: {state.message}</div>;

  const { org, run, role, evaluation, currency, promotions, noun } = state;

  // Mirrors app.can_write + record_evaluation's state check (0004 §6). The
  // database is the boundary (gate 8); this only avoids offering a button the
  // server would refuse.
  const canEvaluate =
    (role === "owner" || role === "lead_assessor" || role === "assessor") &&
    (run.state === "draft" || run.state === "ready_for_review" || run.state === "finalized");

  async function evaluate() {
    setActionError(null);
    setEvaluating(true);
    try {
      await invokeEvaluation(run.id);
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setEvaluating(false);
    }
  }

  const assessPath = `/clients/${org.id}/runs/${run.id}/assess`;
  const snapshot = evaluation?.result ?? null;
  const result: EvaluationResult | null = snapshot?.result ?? null;

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem", flexWrap: "wrap" }}>
        <h1 style={{ marginBottom: 0 }}>Evaluation results</h1>
        <StateBadge state={run.state} />
        <span style={{ flex: 1 }} />
        {canEvaluate && (
          <button className="btn" onClick={() => void evaluate()} disabled={evaluating}>
            {evaluating ? "Evaluating…" : evaluation ? "Re-evaluate" : "Evaluate now"}
          </button>
        )}
      </div>
      <p style={{ color: "var(--text-muted)", marginTop: "0.25rem" }}>
        {run.title ?? "Untitled run"} — results are computed by the evaluation
        engine and shown here unchanged.
      </p>
      {actionError && <div className="error-banner">Evaluation failed: {actionError}</div>}

      {!evaluation && (
        <section className="card" style={{ marginTop: "1rem" }}>
          <h2 style={{ marginTop: 0 }}>Not yet evaluated</h2>
          <p style={{ marginBottom: 0, color: "var(--text-muted)" }}>
            This run has no evaluation result yet. Nothing is shown here until
            the engine has produced one
            {canEvaluate ? " — use “Evaluate now” above." : "."}{" "}
            <Link to={assessPath}>Continue entry in the workspace</Link>.
          </p>
        </section>
      )}

      {evaluation && currency && <CurrencyBanner currency={currency} evaluation={evaluation} />}

      {evaluation && !result && (
        <div className="error-banner" style={{ marginTop: "1rem" }}>
          The stored evaluation snapshot has an unexpected shape and cannot be
          displayed. It has not been altered; report this.
        </div>
      )}

      {evaluation && result && (
        <>
          {!result.validation.ok && (
            <section className="card" style={{ marginTop: "1rem", borderLeft: "3px solid var(--state-danger)" }}>
              <h2 style={{ marginTop: 0 }}>Input validation failed</h2>
              <p style={{ color: "var(--text-muted)" }}>
                These are problems with the assessment input, not with the
                assessed subject. No verdict is issued while they stand (ADR-30).
              </p>
              <IssueList issues={result.validation.issues} />
            </section>
          )}
          {result.validation.ok && result.validation.issues.length > 0 && (
            <details className="card" style={{ marginTop: "1rem" }}>
              <summary style={{ cursor: "pointer" }}>
                {result.validation.issues.length} validation warning
                {result.validation.issues.length === 1 ? "" : "s"}
              </summary>
              <IssueList issues={result.validation.issues} />
            </details>
          )}

          <VerdictCard result={result} noun={noun} />
          <OverallCard result={result} noun={noun} />
          <BlocksCard result={result} assessPath={assessPath} noun={noun} />
          <DimensionsCard result={result} noun={noun} />
          <FindingsCard
            result={result}
            evaluation={evaluation}
            promotions={promotions}
            canPromote={role === "owner" || role === "lead_assessor" || role === "assessor"}
            registerPath={`/clients/${org.id}/runs/${run.id}/register`}
            onPromoted={() => void load()}
          />
          <TechnicalDetails evaluation={evaluation} />
        </>
      )}
    </>
  );
}

// ---- currency (gate 9) --------------------------------------------------

function CurrencyBanner({
  currency,
  evaluation,
}: {
  currency: Currency;
  evaluation: EvaluationRow;
}) {
  const when = new Date(evaluation.evaluated_at).toLocaleString();
  if (currency.kind === "stale") {
    return (
      <div
        className="card"
        style={{ marginTop: "1rem", background: "var(--lf-amber-20)", color: "var(--lf-amber-deep)" }}
      >
        <strong>Stale.</strong> Responses have changed since this evaluation
        ({when}). The result below reflects the responses as they were then —
        re-evaluate to refresh it.
      </div>
    );
  }
  if (currency.kind === "unknown") {
    return (
      <div
        className="card"
        style={{ marginTop: "1rem", background: "var(--lf-amber-20)", color: "var(--lf-amber-deep)" }}
      >
        <strong>Currency unknown.</strong> Whether responses have changed since
        this evaluation ({when}) could not be determined: {currency.reason}. Do
        not treat the result as current.
      </div>
    );
  }
  return (
    <p style={{ marginTop: "1rem", color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
      Current — the responses match the input this evaluation was computed from.
      Evaluated {when}.
    </p>
  );
}

// ---- verdict (ADR-30) ---------------------------------------------------

function VerdictCard({ result, noun }: { result: EvaluationResult; noun: BlockNoun }) {
  const v = result.formal_verdict;
  return (
    <section className="card" style={{ marginTop: "1rem" }}>
      <h2 style={{ marginTop: 0 }}>Formal verdict</h2>
      {v === null ? (
        <p style={{ marginBottom: 0, color: "var(--text-muted)" }}>
          <strong style={{ color: "var(--text-primary)" }}>Withheld.</strong>{" "}
          {result.formal_verdict_withheld ??
            "The engine did not state why - report this."}
        </p>
      ) : (
        <>
          <p style={{ fontSize: "1.6rem", margin: 0 }}>
            Maturity level <strong>{num(v.mean_maturity_level)}</strong>
            <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
              {" "}
              across {v.block_count} contributing {v.block_count === 1 ? noun.one : noun.many}
            </span>{" "}
            <QualBadge q={v.qualification} />
          </p>
          {v.qualification.reasons.length > 0 && (
            <p style={{ color: "var(--text-muted)", margin: "0.35rem 0 0" }}>
              Qualified because{" "}
              {v.qualification.reasons
                .map((r) => REASON_COPY[r] ?? r)
                .join("; ")}
              .
            </p>
          )}
          {v.capped && (
            <p style={{ color: "var(--state-danger)", margin: "0.35rem 0 0" }}>
              Capped{v.cap_label ? ` — ${v.cap_label}` : ""}
              {v.cap_block_ids.length > 0 && (
                <> (caused by {v.cap_block_ids.join(", ")})</>
              )}
              .
            </p>
          )}
        </>
      )}
    </section>
  );
}

// ---- overall (ADR-16, ADR-23) ------------------------------------------

function OverallCard({ result, noun }: { result: EvaluationResult; noun: BlockNoun }) {
  const o = result.overall;
  return (
    <section className="card" style={{ marginTop: "1rem" }}>
      <h2 style={{ marginTop: 0 }}>Overall</h2>
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "0.75rem 1.25rem",
          margin: 0,
        }}
      >
        <div>
          <dt style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>
            Mean maturity level
          </dt>
          <dd style={{ margin: 0, fontSize: "1.25rem" }}>
            {num(o.mean_maturity_level)}
            <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>
              {" "}/ {countOf(o.block_count, noun)}
            </span>
          </dd>
        </div>
        <div>
          <dt style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>
            Mean raw score (kept beside the level)
          </dt>
          <dd style={{ margin: 0, fontSize: "1.25rem" }}>{num(o.mean_raw_score)}</dd>
        </div>
        <div>
          <dt style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>Coverage</dt>
          <dd style={{ margin: 0 }}>
            {o.coverage.blocks_covered}/{o.coverage.blocks_applicable} {noun.many},{" "}
            {o.coverage.criteria_covered}/{o.coverage.criteria_applicable} criteria
          </dd>
        </div>
        <div>
          <dt style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>
            Evidence (reported separately)
          </dt>
          <dd style={{ margin: 0 }}>
            {o.evidence.linked}/{o.evidence.required} required links —{" "}
            {o.evidence.sufficient ? "sufficient" : "insufficient"}
          </dd>
        </div>
        <div>
          <dt style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>Qualification</dt>
          <dd style={{ margin: 0 }}>
            <QualBadge q={o.qualification} />
            {o.qualification.reasons.length > 0 && (
              <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
                {" "}
                {o.qualification.reasons.map((r) => REASON_COPY[r] ?? r).join("; ")}
              </span>
            )}
          </dd>
        </div>
      </dl>
      {o.verdict_cap.applied && (
        <p style={{ color: "var(--state-danger)", marginBottom: 0 }}>
          {o.verdict_cap.label}: {o.verdict_cap.reasons.join("; ")} — caused by{" "}
          {o.verdict_cap.block_ids.join(", ")}.
        </p>
      )}
    </section>
  );
}

// ---- per-block table -------------------------------------------------

function CapChips({ caps }: { caps: CapApplication[] }) {
  if (caps.length === 0) return null;
  return (
    <>
      {caps.map((c) => (
        <span
          key={c.id}
          className="badge"
          title={`${c.reason}${c.criterion_ids.length > 0 ? ` (${c.criterion_ids.join(", ")})` : ""}`}
          style={{
            background: "var(--state-danger-soft)",
            color: "var(--state-danger)",
            marginLeft: "0.35rem",
          }}
        >
          {c.source === "critical_trigger" ? "trigger" : "absence"} cap ≤ {c.max_level}
        </span>
      ))}
    </>
  );
}

function BlocksCard({
  result,
  assessPath,
  noun,
}: {
  result: EvaluationResult;
  assessPath: string;
  noun: BlockNoun;
}) {
  const td: CSSProperties = {
    padding: "0.4rem 0.6rem",
    borderTop: "1px solid var(--border, #e3e3e3)",
    verticalAlign: "top",
  };
  const th: CSSProperties = {
    padding: "0.4rem 0.6rem",
    textAlign: "left",
    color: "var(--text-muted)",
    fontSize: "var(--lf-meta-size)",
    fontWeight: 500,
    whiteSpace: "nowrap",
  };
  return (
    <section className="card" style={{ marginTop: "1rem", overflowX: "auto" }}>
      <h2 style={{ marginTop: 0 }}>{capitalised(noun.many)}</h2>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)", marginTop: 0 }}>
        Raw score and maturity level are both shown (ADR-23); a capped level
        keeps its uncapped value visible so the cap's effect is explicit, not
        inferred. “—” means <em>not scored</em>, which is never a zero.
      </p>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "var(--lf-body-size)" }}>
        <thead>
          <tr>
            <th style={th}>#</th>
            <th style={th}>{capitalised(noun.one)}</th>
            <th style={th}>Applicability</th>
            <th style={th}>Raw</th>
            <th style={th}>Level</th>
            <th style={th}>Coverage</th>
            <th style={th}>Evidence</th>
            <th style={th}>Qualification</th>
            <th style={th}>Trigger</th>
          </tr>
        </thead>
        <tbody>
          {result.blocks.map((b: BlockResult) => (
            <tr
              key={b.block_id}
              style={b.contributes_to_overall ? undefined : { color: "var(--text-muted)" }}
            >
              <td style={td}>{b.sequence}</td>
              <td style={td}>
                <Link to={`${assessPath}?block=${encodeURIComponent(b.block_id)}`}>
                  {b.title}
                </Link>
                {b.is_critical && (
                  <span title={`critical ${noun.one}`} style={{ color: "var(--state-danger)" }}>
                    {" "}▲
                  </span>
                )}
                {!b.contributes_to_overall && (
                  <span style={{ fontSize: "var(--lf-meta-size)" }}> (not in overall)</span>
                )}
              </td>
              <td style={td}>{b.applicability.replaceAll("_", " ")}</td>
              <td style={td} title={b.calculated_raw_score === null ? "not scored" : undefined}>
                {num(b.calculated_raw_score)}
              </td>
              <td style={td}>
                {b.calculated_maturity_level === null ? (
                  <span title="not scored">—</span>
                ) : b.caps_applied.length > 0 ? (
                  <>
                    <s style={{ color: "var(--text-muted)" }}>{b.uncapped_maturity_level}</s>{" "}
                    <strong>{b.calculated_maturity_level}</strong>
                  </>
                ) : (
                  <strong>{b.calculated_maturity_level}</strong>
                )}
                <CapChips caps={b.caps_applied} />
              </td>
              <td style={td}>
                {b.coverage.scored}/{b.coverage.required} scored{" "}
                {b.coverage.met ? (
                  <span style={{ color: "var(--state-ok)" }}>✓</span>
                ) : (
                  <span style={{ color: "var(--lf-amber-deep)" }}>below minimum</span>
                )}
              </td>
              <td style={td}>
                {b.evidence.required === 0 ? (
                  <span style={{ color: "var(--text-muted)" }}>none required</span>
                ) : (
                  <>
                    {b.evidence.linked}/{b.evidence.required}
                    {!b.evidence.sufficient && (
                      <span style={{ color: "var(--lf-amber-deep)" }}> insufficient</span>
                    )}
                  </>
                )}
              </td>
              <td style={td}>
                <QualBadge q={b.qualification} />
              </td>
              <td style={td}>
                {b.trigger.fired ? (
                  <span style={{ color: "var(--state-danger)" }}>fired</span>
                ) : b.trigger.confirmed ? (
                  <span
                    style={{ color: "var(--lf-amber-deep)" }}
                    title="confirmed by the assessor, but the evidence requirement was not met, so it did not fire"
                  >
                    confirmed, not fired
                  </span>
                ) : (
                  <span style={{ color: "var(--text-muted)" }}>no</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// ---- dimensions (ADR-25: never without denominators) --------------------

function DimensionsCard({ result, noun }: { result: EvaluationResult; noun: BlockNoun }) {
  if (result.dimensions.length === 0) return null;
  return (
    <section className="card" style={{ marginTop: "1rem" }}>
      <h2 style={{ marginTop: 0 }}>Dimensions</h2>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)", marginTop: 0 }}>
        Dimension means are diagnostic and never affect the maturity
        calculation. Every mean travels with its denominator.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "0.6rem" }}>
        {result.dimensions.map((d) => (
          <div
            key={d.dimension}
            style={{
              border: "1px solid var(--border, #e3e3e3)",
              borderRadius: "var(--radius)",
              padding: "0.5rem 0.7rem",
            }}
          >
            <div style={{ fontSize: "var(--lf-meta-size)", color: "var(--text-muted)" }}>
              {d.dimension} — {d.title}
            </div>
            <div style={{ fontSize: "1.15rem" }}>
              {d.mean === null ? (
                <span style={{ color: "var(--text-muted)" }} title="tested by the instrument, not yet scored">
                  not yet scored
                </span>
              ) : (
                num(d.mean)
              )}
            </div>
            <div style={{ fontSize: "var(--lf-meta-size)", color: "var(--text-muted)" }}>
              {d.criterion_count}/{d.defined_criterion_count} criteria ·{" "}
              {d.artifact_count}/{d.defined_artifact_count} {noun.many}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---- findings (kernel-raised) + promotion into the register (F4.3/F4.4) --

function FindingsCard({
  result,
  evaluation,
  promotions,
  canPromote,
  registerPath,
  onPromoted,
}: {
  result: EvaluationResult;
  evaluation: EvaluationRow;
  promotions: PromotionState;
  canPromote: boolean;
  registerPath: string;
  onPromoted: () => void;
}) {
  const [promoting, setPromoting] = useState<string | null>(null); // kernel key
  return (
    <section className="card" style={{ marginTop: "1rem" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem", flexWrap: "wrap" }}>
        <h2 style={{ marginTop: 0, flex: 1 }}>Findings</h2>
        <Link to={registerPath} style={{ fontSize: "var(--lf-body-size)" }}>
          Findings &amp; actions register →
        </Link>
      </div>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)", marginTop: 0 }}>
        Engine findings are fixed evaluation output. Promoting one creates
        a managed finding in the register with full provenance; it never
        changes this result.
      </p>
      {result.findings.length === 0 ? (
        <p style={{ color: "var(--text-muted)", marginBottom: 0 }}>
          The engine raised no findings on this evaluation.
        </p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
          {result.findings.map((f) => {
            const promoted = promotions.activeKeys.has(f.id);
            const generation = promotions.generationByKey.get(f.id);
            return (
              <li key={f.id} style={{ marginBottom: "0.4rem" }}>
                <span
                  className="badge"
                  style={{
                    background: "transparent",
                    border: `1px solid ${SEVERITY_COLOR[f.severity]}`,
                    color: SEVERITY_COLOR[f.severity],
                    marginRight: "0.4rem",
                  }}
                >
                  {f.severity}
                </span>
                {f.text}{" "}
                <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>
                  ({f.block_id}
                  {f.criterion_ids.length > 0 ? `: ${f.criterion_ids.join(", ")}` : ""})
                </span>{" "}
                {promoted ? (
                  <span
                    className="badge badge-role"
                    title={`This engine finding has a managed expression in the register (promotion ${generation}).`}
                  >
                    in register
                  </span>
                ) : canPromote && promoting !== f.id ? (
                  <button className="btn btn-secondary" onClick={() => setPromoting(f.id)}>
                    Promote to register{generation ? " (again)" : ""}
                  </button>
                ) : null}
                {promoting === f.id && (
                  <PromoteForm
                    finding={f}
                    evaluation={evaluation}
                    priorGeneration={generation ?? 0}
                    onDone={() => {
                      setPromoting(null);
                      onPromoted();
                    }}
                    onCancel={() => setPromoting(null)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
      {result.recommendations.length > 0 && (
        <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)", marginBottom: 0 }}>
          {result.recommendations.length} corrective-action proposal
          {result.recommendations.length === 1 ? "" : "s"} travel with this
          result; deterministic proposals stay proposals — creating owned
          actions is done in the register.
        </p>
      )}
    </section>
  );
}

function PromoteForm({
  finding,
  evaluation,
  priorGeneration,
  onDone,
  onCancel,
}: {
  finding: Finding;
  evaluation: EvaluationRow;
  priorGeneration: number;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState(finding.text);
  const [severity, setSeverity] = useState<Finding["severity"]>(finding.severity);
  const [rationale, setRationale] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A mechanical hint only — the DATABASE decides when a rationale is
  // required (wording or severity differs, or the source evaluation is
  // stale) and its refusal renders verbatim below. The hint just spares a
  // round-trip for the two locally visible causes.
  const differs = description !== finding.text || severity !== finding.severity;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await promoteKernelFinding({
        evaluationId: evaluation.id,
        kernelFindingKey: finding.id,
        title: title.trim(),
        managedDescription: description === finding.text ? null : description,
        severity: severity === finding.severity ? null : severity,
        rationale: rationale.trim() || null,
      });
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        border: "1px solid var(--border, #e3e3e3)",
        borderRadius: "var(--radius)",
        padding: "0.6rem 0.8rem",
        margin: "0.4rem 0",
        display: "grid",
        gap: "0.5rem",
        maxWidth: "40rem",
      }}
    >
      {priorGeneration > 0 && (
        <p style={{ margin: 0, color: "var(--lf-amber-deep)", fontSize: "var(--lf-body-size)" }}>
          Every earlier managed expression of this engine finding is
          withdrawn; this is promotion {priorGeneration + 1}.
        </p>
      )}
      <input
        placeholder="Register title (required)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        aria-label="Managed finding title"
      />
      <NoNamesHint />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        aria-label="Managed wording"
      />
      <NoNamesHint />
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
          Severity{" "}
          <select value={severity} onChange={(e) => setSeverity(e.target.value as Finding["severity"])}>
            {(["critical", "high", "medium", "low"] as const).map((s) => (
              <option key={s} value={s}>
                {s}
                {s === finding.severity ? " (engine)" : ""}
              </option>
            ))}
          </select>
        </label>
      </div>
      <input
        placeholder={
          differs
            ? "Rationale (required — the managed expression differs from the engine original)"
            : "Rationale (required when wording/severity differ, or a newer evaluation exists)"
        }
        value={rationale}
        onChange={(e) => setRationale(e.target.value)}
        aria-label="Promotion rationale"
      />
      <NoNamesHint />
      {error && <div className="error-banner">{error}</div>}
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button className="btn" disabled={busy || title.trim() === ""} onClick={() => void submit()}>
          {busy ? "Promoting…" : "Promote"}
        </button>
        <button className="btn btn-secondary" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---- audit drawer -------------------------------------------------------

function TechnicalDetails({ evaluation }: { evaluation: EvaluationRow }) {
  const rows: [string, string][] = [
    ["Evaluation id", evaluation.id],
    ["Sequence", String(evaluation.seq)],
    ["Evaluated at", new Date(evaluation.evaluated_at).toLocaleString()],
    ["Engine contract", evaluation.engine_contract_version],
    ["Policy id", evaluation.policy_id],
    ["Canonicalization", evaluation.canonicalization],
    ["Hash algorithm", evaluation.hash_algorithm],
    ["Definition hash", evaluation.definition_hash],
    ["Policy hash", evaluation.policy_hash],
    ["Input hash", evaluation.input_hash],
    ["Result hash", evaluation.result_hash],
  ];
  return (
    <details style={{ marginTop: "1rem" }}>
      <summary style={{ cursor: "pointer", color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
        Technical details (replay identity)
      </summary>
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "max-content 1fr",
          gap: "0.3rem 1.25rem",
          margin: "0.5rem 0 0",
          fontSize: "var(--lf-meta-size)",
        }}
      >
        {rows.map(([k, v]) => (
          <div key={k} style={{ display: "contents" }}>
            <dt style={{ color: "var(--text-muted)" }}>{k}</dt>
            <dd style={{ margin: 0, fontFamily: "var(--lf-font-mono)", overflowWrap: "anywhere" }}>{v}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

// ---- validation issues --------------------------------------------------

function IssueList({
  issues,
}: {
  issues: EvaluationResult["validation"]["issues"];
}) {
  return (
    <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
      {issues.map((i, idx) => (
        <li key={idx} style={{ marginBottom: "0.3rem" }}>
          <strong style={{ color: i.severity === "error" ? "var(--state-danger)" : "var(--lf-amber-deep)" }}>
            {i.severity}
          </strong>{" "}
          <code>{i.code}</code> {i.message}
          {(i.block_id || i.criterion_id) && (
            <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>
              {" "}
              ({i.block_id}
              {i.criterion_id ? `: ${i.criterion_id}` : ""})
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
